import { useEffect, useMemo } from 'react';
import Immutable from 'immutable';
import { dispatch, useReframe } from 'nike-re-framejs';
import { ChannelStatusResponse } from './types';
import { nuiCtx } from '../../../common/initialization';
import { useComponentId } from '../../../common/hooks/useComponentId';
import { isCashinOfferDisabled, isCashinTransactionRunning } from '../../turboLive/components/cashin/subs';
import { usePushConnectionStatus } from '../../../common/push/usePushConnectionStatus';
import { isProfileCashinChannelStatusEnabled } from '../../../profiles';
import { changeStatusToKey, isCashinEnabledByChannelStatusKey$$, isChannelStatusEnabled } from './cashin-utils';

let pushSubscription;
let subscribedChannelStatusKeys = new Map<number, string[]>();

function getUniqueChannelStatusKeys(): Set<string> {
    const uniqueChannelStatusKeys = new Set<string>();
    subscribedChannelStatusKeys.forEach(keys => keys.forEach(key => uniqueChannelStatusKeys.add(key)));
    return uniqueChannelStatusKeys;
}

export function useIsCashinDisabled(cashinOffer: Immutable.Map<string, any>): boolean {
    const componentId = useComponentId();
    const isOfferDisabled = isCashinOfferDisabled(cashinOffer?.get('status'));
    const channelStatusIds: Immutable.List<any> = isOfferDisabled
        ? Immutable.List()
        : cashinOffer?.get('channelStatusIds');
    const channelStatusKeys: string[] = useMemo(
        () =>
            (channelStatusIds || Immutable.List())
                .toJS()
                .map(changeStatusToKey)
                .sort(),
        [channelStatusIds]
    );
    useEffect(() => {
        if (!channelStatusKeys?.length || !isProfileCashinChannelStatusEnabled()) {
            return () => {};
        }
        subscribedChannelStatusKeys.set(componentId, channelStatusKeys);
        startCashinChannelStatusPush();
        resetPushPaths();
        return () => {
            subscribedChannelStatusKeys.delete(componentId);
            resetPushPaths();
        };
    }, [componentId, channelStatusKeys]);
    const { subscribe } = useReframe();
    const isChannelStatusEnabled =
        subscribe(isCashinEnabledByChannelStatusKey$$(channelStatusKeys)) || !isProfileCashinChannelStatusEnabled();
    const { isPushDisconnected } = usePushConnectionStatus();
    return (
        !cashinOffer ||
        !isChannelStatusEnabled ||
        isPushDisconnected ||
        isCashinTransactionRunning(cashinOffer.getIn(['activeTransaction', 'status']))
    );
}

export function startCashinChannelStatusPush() {
    if (pushSubscription) {
        return;
    }
    pushSubscription = nuiCtx.pushClient.subscription({
        onMessage(msg) {
            const channelStatus = msg as ChannelStatusResponse;
            channelStatus && dispatch(handleChannelStatusUpdate([channelStatus]));
        },
        onSnapshot(snapshots) {
            const channelStatuses = snapshots as ChannelStatusResponse[];
            channelStatuses && dispatch(handleChannelStatusUpdate(channelStatuses));
        }
    });

    setInterval(() => dispatch(handleCheckValidity), 1000);
}

export function resetPushPaths() {
    pushSubscription.resetPaths(Array.from(getUniqueChannelStatusKeys()).map(key => `/ticketus/channel/${key}/`));
}

/**
 * Private handlers
 */
const handleChannelStatusUpdate = (channelStatuses: ChannelStatusResponse[]) => (db: Immutable.Map<string, any>) =>
    (channelStatuses || []).reduce(
        (newDb, channelStatus) =>
            newDb.updateIn(
                ['ticketus', 'cashin', 'channelStatus', changeStatusToKey(channelStatus)],
                (oldStatus?: ChannelStatusResponse) => {
                    if (!!isChannelStatusEnabled(oldStatus) !== !!isChannelStatusEnabled(channelStatus)) {
                        const status = isChannelStatusEnabled(channelStatus) ? 'enabled' : 'disbled';
                        console.log(
                            `Channel is now ${status}: channelId=${channelStatus.c} game=${channelStatus.g} partition=${channelStatus.p}`
                        );
                    }
                    return channelStatus;
                }
            ),
        db
    );

const handleCheckValidity = (db: Immutable.Map<string, any>) =>
    db.updateIn(
        ['ticketus', 'cashin', 'channelStatus'],
        Immutable.Map(),
        (channelStatuses: Immutable.Map<string, ChannelStatusResponse>) => {
            const subscribedChannelStatusKeys = getUniqueChannelStatusKeys();
            return channelStatuses.filter(channelStatus => {
                if (isChannelStatusEnabled(channelStatus)) {
                    return true;
                }
                if (subscribedChannelStatusKeys.has(changeStatusToKey(channelStatus))) {
                    console.log(
                        `Channel is now delayed: channelId=${channelStatus.c} game=${channelStatus.g} partition=${channelStatus.p}`
                    );
                }
                return false;
            });
        }
    );
